import { createHash } from 'crypto';

import moment from 'moment';
import { NextRequest } from 'next/server';

import { InternalBidAndOffer, PriceType } from '@/backend-api/Api';
import { Option as SelectOption } from '@/components/ui/multiple-select';
import { Country, LocationCollection, MarketReportTemplate, NewsContentCategory, Product, User } from '@/prisma/types';
import { ExtendedSubscriptionTemplate, ExtendedUser } from '@/prisma/types';

import { SelectOptionVM } from './models/view-models/view-models';

export { cn } from './css-utils';

export function getLocalStorage(key: string, defaultValue: boolean | undefined) {
    const stickyValue = localStorage.getItem(key);

    return stickyValue !== null && stickyValue !== 'undefined' ? JSON.parse(stickyValue) : defaultValue;
}

export function setLocalStorage(key: string, value: boolean | undefined) {
    localStorage.setItem(key, JSON.stringify(value));
}

export const countriesToOptions = (countries: Country[]) => {
    const countryOptions: SelectOption[] =
        countries?.map((country: Country) => {
            return {
                label: country.displayName,
                value: country.displayName,
            } as SelectOption;
        }) ?? [];

    return countryOptions;
};

export const locationCollectionsToOptions = (locationCollections: LocationCollection[]) => {
    const locationCollectionOptions: SelectOption[] =
        locationCollections?.map((locationCollection: LocationCollection) => {
            return {
                label: locationCollection.name,
                value: locationCollection.name,
            } as SelectOption;
        }) ?? [];

    return locationCollectionOptions;
};

export const productsToOptions = (products: Product[]) => {
    const productOptions: SelectOption[] =
        products?.map((product: Product) => {
            return {
                label: product.name,
                value: product.name,
            } as SelectOption;
        }) ?? [];

    return productOptions;
};

export const categoriesToOptions = (categories: NewsContentCategory[]) => {
    const categoryOptions: SelectOption[] =
        categories?.map((category: NewsContentCategory) => {
            return {
                label: category.name,
                value: category.name,
            } as SelectOption;
        }) ?? [];

    return categoryOptions;
};

export const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.toLowerCase().slice(1);
};

export const dateToString = (date: Date) => {
    return moment(date).format('MMM DD, YYYY');
};

export const dateToMonthString = (date: Date) => {
    return moment(date).format('MMM');
};

export const timeToString = (date: Date) => {
    return moment(date).format('hh:mm A');
};

export const dateAndTimeToString = (date: Date) => {
    return moment(date).format('MMM DD, YYYY hh:mm A');
};

export const dateAndTimeToCondensedString = (date: Date) => {
    return moment(date).format('DD/MM/YYYY HH:mm');
};

export const dateToCondensedString = (date: Date) => {
    return moment(date).format('DD/MM/YYYY');
};

export const dateToCondensedMonthString = (date: Date) => {
    return moment(date).format('DD/MM');
};

export const daysSinceDate = (date: Date) => {
    return moment().diff(date, 'days');
};

export const daysUntilDate = (date: Date) => {
    return moment(date).diff(moment(), 'days');
};

export const parseDate = (dateString: string | undefined): Date | undefined => {
    if (!dateString) return undefined;
    return new Date(dateString);
};

/**
 * Parses a date-only string (e.g. "2025-08-01") as a UTC Date object without timezone offset issues.
 * If input is null or undefined, returns null.
 *
 * @param dateString - A date string in "YYYY-MM-DD" format
 * @returns Date object in UTC or null if invalid
 */
export function parseDateStringAsUTC(dateString: string | null | undefined): Date | null {
    if (!dateString) return null;

    const parts = dateString.split('-');
    if (parts.length !== 3) {
        console.error(`Invalid date string format: ${dateString}`);
        return null;
    }

    const [year, month, day] = parts.map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day) || month < 1 || month > 12 || day < 1 || day > 31) {
        console.error(`Invalid date components in string: ${dateString}`);
        return null;
    }

    // Construct UTC date explicitly
    return new Date(Date.UTC(year, month - 1, day));
}

export const formatFreightDateFromInternalBidAndOffer = (
    internalBidAndOffer: InternalBidAndOffer | undefined,
    includeHalfMonths: boolean = true
) => {
    if (!internalBidAndOffer) {
        return null;
    }

    const fromDateString =
        internalBidAndOffer.priceType === PriceType.PriceTypeFLAT
            ? internalBidAndOffer.flatPriceInformation.shipmentPeriodStart
            : internalBidAndOffer.premiumPriceInformation.shipmentPeriodStart;
    const toDateString =
        internalBidAndOffer.priceType === PriceType.PriceTypeFLAT
            ? internalBidAndOffer.flatPriceInformation.shipmentPeriodEnd
            : internalBidAndOffer.premiumPriceInformation.shipmentPeriodEnd;

    if (!fromDateString || !toDateString) {
        console.error('Shipment date undefined');
        return null;
    }

    const fromDate = parseDateStringAsUTC(fromDateString);
    const toDate = parseDateStringAsUTC(toDateString);

    if (!fromDate || !toDate) {
        console.error('Invalid date format');
        return '';
    }

    return formatFreightDate(fromDate, toDate, includeHalfMonths);
};

export const formatFreightDateFromStrings = (
    fromDateString: string | null,
    toDateString: string | null,
    includeHalfMonths: boolean = true
) => {
    if (!fromDateString || !toDateString) {
        console.error('Shipment date undefined');
        return null;
    }

    const fromDate = parseDateStringAsUTC(fromDateString);
    const toDate = parseDateStringAsUTC(toDateString);

    if (!fromDate || !toDate) {
        console.error('Invalid date format');
        return '';
    }

    return formatFreightDate(fromDate, toDate, includeHalfMonths);
};

export const formatFreightDate = (fromDate: Date, toDate: Date, includeHalfMonths: boolean = true) => {
    const currentYear = new Date().getUTCFullYear();

    const fromDay = fromDate.getUTCDate();
    const toDay = toDate.getUTCDate();

    const fromMonthIdx = fromDate.getUTCMonth();
    const toMonthIdx = toDate.getUTCMonth();

    const fromYear = fromDate.getUTCFullYear();
    const toYear = toDate.getUTCFullYear();

    const fromMonth = moment.utc(fromDate).format('MMM');
    const toMonth = moment.utc(toDate).format('MMM');

    const fromHalf = fromDay < 15 ? '1H' : '2H';
    const toHalf = toDay <= 15 ? '1H' : '2H';

    const sameMonth = fromMonthIdx === toMonthIdx && fromYear === toYear;

    const yearSuffix = (year: number) => (year !== currentYear ? ` ${String(year).slice(-2)}` : '');

    if (sameMonth) {
        if (fromHalf === toHalf) {
            return `${includeHalfMonths ? fromHalf : ''} ${fromMonth}${yearSuffix(fromYear)}`;
        } else {
            return `${fromMonth}${yearSuffix(fromYear)}`;
        }
    }
    const fromLabel =
        fromHalf === '2H'
            ? `${includeHalfMonths ? '2H ' : ''}${fromMonth}${yearSuffix(fromYear)}`
            : `${fromMonth}${yearSuffix(fromYear)}`;
    const toLabel =
        toHalf === '1H'
            ? `${includeHalfMonths ? '1H ' : ''}${toMonth}${yearSuffix(toYear)}`
            : `${toMonth}${yearSuffix(toYear)}`;
    return `${fromLabel} - ${toLabel}`;
};

export const formatPeriodExactString = (bidOffer: InternalBidAndOffer) => {
    const fromDateString =
        bidOffer.priceType === PriceType.PriceTypeFLAT
            ? bidOffer.flatPriceInformation.shipmentPeriodStart
            : bidOffer.premiumPriceInformation.shipmentPeriodStart;
    const toDateString =
        bidOffer.priceType === PriceType.PriceTypeFLAT
            ? bidOffer.flatPriceInformation.shipmentPeriodEnd
            : bidOffer.premiumPriceInformation.shipmentPeriodEnd;

    if (!fromDateString || !toDateString) {
        console.error('Shipment date undefined');
        return '';
    }

    const fromDate = parseDateStringAsUTC(fromDateString);
    const toDate = parseDateStringAsUTC(toDateString);

    if (!fromDate || !toDate) {
        console.error('Invalid date format');
        return '';
    }

    const utcFromDateString = moment.utc(fromDate).format('DD/MM');
    const utcToDateString = moment.utc(toDate).format('DD/MM');
    return `${utcFromDateString} - ${utcToDateString}`;
};

export const parseDateWithFormat = (
    dateString: string | undefined,
    format: string = 'DD/MM/YYYY'
): Date | undefined => {
    if (!dateString) return undefined;

    // Parse the date string with the expected format
    const date = moment(dateString, format, true);

    if (!date.isValid()) {
        console.error('Invalid date format');
        return undefined;
    }

    // Return the JavaScript Date object, with time set to 00:00:00
    const parsedDate = new Date(date.year(), date.month(), date.date());

    return parsedDate;
};

export const addMonthsToDate = ({ initialDate, months }: { initialDate: Date; months: number }) => {
    // Get today's date, add months, and convert to a JavaScript Date object
    return moment(initialDate).add(months, 'months').toDate();
};

export const addDayToDate = ({ initialDate, days }: { initialDate: Date; days: number }) => {
    // Get today's date, add months, and convert to a JavaScript Date object
    return moment(initialDate).add(days, 'days').toDate();
};

export const startOfDay = () => {
    // Get today's date, add months, and convert to a JavaScript Date object
    return moment().utc().startOf('day').toDate();
};

export const getUTCDateOnly = (date: Date): Date => {
    if (!date) {
        console.error('getUTCDateOnly: date is undefined or null');
        return new Date();
    }
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};

export const midDay = (date: Date) => {
    const newDate = new Date(date);
    newDate.setHours(12, 0, 0, 0);
    return newDate;
};

export const getMiddleOfMonth = (monthStart: Date, monthEnd: Date) => {
    const monthMiddle = monthEnd.getTime() - monthStart.getTime();

    const monthMiddleDate = midDay(new Date(monthStart.getTime() + monthMiddle / 2));

    return monthMiddleDate;
};

/**
 * Normalize the date to the start of the day in UTC.
 * This function takes the provided date, converts it to the equivalent in UTC, and sets it to midnight.
 *
 * @param date - The date object to normalize.
 * @returns A moment instance set to midnight in UTC.
 */
export function normalizeToUtcMidnight(date: Date): Date {
    const year = date.getFullYear();
    const month = date.getMonth(); // Months are zero-based (0 = January, 11 = December)
    const day = date.getDate();

    const dateOnlyUtc = new Date(Date.UTC(year, month, day));
    // console.log("isoString:", dateOnlyUtc.toISOString()); // e.g., 2024-10-25T00:00:00.000Z (UTC time)
    return dateOnlyUtc;
}

/**
 * Format the date for display as if it were in UTC.
 * @param date - The date to format.
 * @param formatStr - The desired display format.
 * @returns A formatted date string.
 */
export function formatFromUtcMidnight(date: Date, formatStr: string): string {
    // Use moment to format the date in UTC time for consistent display
    return moment.utc(date).format(formatStr);
}

export const sortByDateStrings = (rowADateString: string | undefined, rowBDateString: string | undefined) => {
    const dateA = parseDate(rowADateString);
    const dateB = parseDate(rowBDateString);

    if (!dateA || !dateB) return 0;
    return dateA.getTime() - dateB.getTime();
};

export const sortByDate = (rowADate: Date | undefined, rowBDate: Date | undefined) => {
    if (!rowADate || !rowBDate) return 0;
    return rowADate.getTime() - rowBDate.getTime();
};

export const getBadgeColor = (marketReportTemplate: MarketReportTemplate) => {
    switch (marketReportTemplate.interval) {
        case 'DAILY':
            return 'bg-[#D7BF5C]';
        case 'WEEKLY':
            return 'bg-[#C695C7]';
        case 'MONTHLY':
            return 'bg-[#ACCBE7]';
        case 'QUARTERLY':
            return 'bg-[#ACE7B2]';
        case 'YEARLY':
            return 'bg-[#E7ACAC]';
        default:
            return 'bg-[#D7BF5C]';
    }
};

export const hashEmail = (email: string) => {
    return createHash('md5').update(email.toLowerCase()).digest('hex');
};

export const filterTemplatesByDuration = (subscriptionTemplates: ExtendedSubscriptionTemplate[], duration: number) => {
    return (
        subscriptionTemplates.map((template) => {
            const defaultSubscriptionPrices = template.defaultSubscriptionPrices?.filter(
                (price) => price.duration === duration
            );
            return {
                ...template,
                defaultSubscriptionPrices: defaultSubscriptionPrices,
            };
        }) ?? []
    );
};

export async function verifyApiKey(request: NextRequest): Promise<boolean> {
    const apiKey = '04942607-acac-4448-bf86-d6d2bed5d0cc'; // Get the API key from the environment variable
    const { searchParams } = new URL(request.url); // Extract query params from the URL

    const requestApiKey = searchParams.get('api_key'); // Get the API key from URL parameters

    return requestApiKey === apiKey; // Return true if keys match
}

export const generateTestEmail = (actualEmail: string) => {
    const emailParts = actualEmail.split('@');

    return `cmnavigatortest+${emailParts[0]}@gmail.com`;
};

export const getUserName = (user: User | ExtendedUser | null) => {
    if (!user) return 'unknown';

    // check if names are null and format correctly
    if (user.firstName && user.lastName) {
        return `${user.firstName} ${user.lastName}`;
    } else if (user.firstName && !user.lastName) {
        return user.firstName;
    } else if (!user.firstName && user.lastName) {
        return user.lastName;
    } else {
        return 'unknown';
    }
};

export const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
        case 'active':
            return 'bg-green-500';
        case 'canceled':
            return 'bg-red-500';
        case 'expired':
            return 'bg-red-500';
        case 'inactive':
            return 'bg-yellow-500';
        case 'UPCOMING':
            return 'bg-blue-500';
        default:
            return 'bg-gray-500';
    }
};

export const hashColorFromString = (
    str: string,
    opacity: number = 1,
    baseHue: number = 146, // brand hue (e.g. 210° = blue)
    hueRange: number = 360, // how far colors can deviate
    saturation: number = 88, // percent
    lightness: number = 66 // percent
): string => {
    // hash the string deterministically
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Map hash to a hue offset within [-hueRange/2, +hueRange/2]
    const hueOffset = (hash % hueRange) - hueRange / 2;
    const hue = (baseHue + hueOffset + 360) % 360;

    // Clamp opacity
    const clampedOpacity = Math.max(0, Math.min(1, opacity));

    // Convert HSL → RGB
    const [r, g, b] = hslToRgb(hue / 360, saturation / 100, lightness / 100);

    return `rgba(${r}, ${g}, ${b}, ${clampedOpacity})`;
};

// --- helper ---
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    let r: number, g: number, b: number;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const hue2rgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

export const mapSelectOptionVM = (option: SelectOptionVM): SelectOption => {
    return {
        label: option.displayName,
        value: option.key,
    };
};

export const mapSelectOptionVMs = (options: SelectOptionVM[] | undefined): SelectOption[] => {
    if (!options) return [];

    const mappedOptions = options.map(mapSelectOptionVM);

    return mappedOptions;
};
